/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  watch,
  onUnmounted,
} from "vue";
import type { Reservation, ReservationRequest } from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { ReservationDetails } from "../ReservationsSchedule/components/ReservationDetails";
import { ReservationRequestDetails } from "../ReservationTabs/ReservationRequestDetails";
import {
  PageHeader,
  type ReservationStatus,
  type RequestStatus,
} from "./components/PageHeader";
import { LoadingState } from "./components/LoadingState";
import { ErrorState } from "./components/ErrorState";
import { EmptyState } from "./components/EmptyState";
import { ContentList } from "./components/ContentList";
import type { TabValue } from "./config";
import { TAB_CONFIG } from "./config";
import type { DataState } from "./types";
import { fetchData, loadMore as loadMoreData } from "./dataFetching";
import {
  DateRangePicker,
  getDateRange,
  type DateRange,
  BottomSheet,
  useLocalization,
} from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

export const ReservationsHistoryPage = defineComponent({
  name: "ReservationsHistoryPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();

    // UI state
    const activeTab = ref<TabValue>("reservations");
    const isSearchOpen = ref(false);
    const searchQuery = ref("");
    const isFiltersOpen = ref(false);
    const scrollContainerRef = ref<HTMLElement | null>(null);

    // Initialize with last 90 days
    const initialRange = getDateRange("last_12m");
    const dateRange = ref<DateRange>({
      ...initialRange,
      type: "last_12m",
    });

    // Status filters
    const reservationStatus = ref<ReservationStatus>("all");
    const requestStatus = ref<RequestStatus>("all");

    // Abort controller for cancelling in-flight requests
    const abortController = ref<AbortController | null>(null);

    // Sheet states
    const selectedRequest = ref<ReservationRequest | null>(null);
    const selectedReservation = ref<Reservation | null>(null);
    const showRequestDetailsSheet = ref(false);
    const showReservationDetailsSheet = ref(false);

    // Data state
    const dataState = ref<DataState>({
      requests: [],
      reservations: [],
      loading: true,
      loadingMore: false,
      error: "",
      pagination: {
        offset: 0,
        limit: 50,
        hasMore: true,
      },
    });

    // Computed values
    const config = computed(() => TAB_CONFIG[activeTab.value]);
    const isRequestView = computed(() => config.value.type === "request");

    const hasData = computed(() => {
      return isRequestView.value
        ? dataState.value.requests.length > 0
        : dataState.value.reservations.length > 0;
    });

    const totalCount = computed(() => {
      return isRequestView.value
        ? dataState.value.requests.length
        : dataState.value.reservations.length;
    });

    const statusCounts = computed(() => {
      const counts: Record<string, number> = {};
      if (isRequestView.value) {
        for (const req of dataState.value.requests) {
          counts[req.status] = (counts[req.status] || 0) + 1;
        }
      } else {
        for (const res of dataState.value.reservations) {
          counts[res.status] = (counts[res.status] || 0) + 1;
        }
      }
      return counts;
    });

    // Data loading
    const loadData = async () => {
      // Abort any in-flight request
      if (abortController.value) {
        abortController.value.abort();
      }
      abortController.value = new AbortController();

      await fetchData(trpc, dataState.value, abortController.value.signal, {
        tab: activeTab.value,
        searchQuery: searchQuery.value,
        dateRange: dateRange.value,
        reservationStatus: reservationStatus.value,
        requestStatus: requestStatus.value,
      });
    };

    const handleLoadMore = async () => {
      await loadMoreData(trpc, dataState.value, abortController.value?.signal, {
        tab: activeTab.value,
        searchQuery: searchQuery.value,
        dateRange: dateRange.value,
        reservationStatus: reservationStatus.value,
        requestStatus: requestStatus.value,
      });
    };

    // Event handlers
    const handleTabChange = (tab: TabValue) => {
      activeTab.value = tab;
    };

    const handleDateRangeChange = (range: DateRange) => {
      dateRange.value = range;
    };

    const handleFiltersToggle = () => {
      isFiltersOpen.value = !isFiltersOpen.value;
    };

    const handleReservationStatusChange = (status: ReservationStatus) => {
      reservationStatus.value = status;
    };

    const handleRequestStatusChange = (status: RequestStatus) => {
      requestStatus.value = status;
    };

    const handleSearchToggle = () => {
      isSearchOpen.value = !isSearchOpen.value;
      if (!isSearchOpen.value) {
        searchQuery.value = "";
      }
    };

    const handleSearchChange = (query: string) => {
      searchQuery.value = query;
    };

    const handleRequestClick = (request: ReservationRequest) => {
      selectedRequest.value = request;
      showRequestDetailsSheet.value = true;
    };

    const handleReservationClick = (reservation: Reservation) => {
      selectedReservation.value = reservation;
      showReservationDetailsSheet.value = true;
    };

    const handleReservationUpdate = (updatedReservation: Reservation) => {
      // Update reservation in place
      const index = dataState.value.reservations.findIndex(
        (r) => r.id === updatedReservation.id,
      );
      if (index !== -1) {
        dataState.value.reservations[index] = updatedReservation;
      }
      if (
        selectedReservation.value &&
        updatedReservation &&
        selectedReservation.value.id == updatedReservation.id
      ) {
        selectedReservation.value = updatedReservation;
      }
    };

    const handleReservationCancel = (cancelledReservation: Reservation) => {
      // Remove the cancelled reservation from the list
      const index = dataState.value.reservations.findIndex(
        (r) => r.id === cancelledReservation.id,
      );
      if (index !== -1) {
        dataState.value.reservations = dataState.value.reservations.filter(
          (r) => r.id !== cancelledReservation.id,
        );
      }
    };

    const handleReservationRequestCancel = (
      cancelledRequest: ReservationRequest,
    ) => {
      // Remove the cancelled request from the list
      const index = dataState.value.requests.findIndex(
        (r) => r.id === cancelledRequest.id,
      );
      if (index !== -1) {
        dataState.value.requests = dataState.value.requests.filter(
          (r) => r.id !== cancelledRequest.id,
        );
      }
    };

    const handleReservationRequestAccept = (
      acceptedRequest: ReservationRequest,
    ) => {
      // Remove the accepted request from the list
      const index = dataState.value.requests.findIndex(
        (r) => r.id === acceptedRequest.id,
      );
      if (index !== -1) {
        dataState.value.requests = dataState.value.requests.filter(
          (r) => r.id !== acceptedRequest.id,
        );
      }
    };

    const handleReservationRequestReject = (
      rejectedRequest: ReservationRequest,
    ) => {
      // Remove the rejected request from the list
      const index = dataState.value.requests.findIndex(
        (r) => r.id === rejectedRequest.id,
      );
      if (index !== -1) {
        dataState.value.requests = dataState.value.requests.filter(
          (r) => r.id !== rejectedRequest.id,
        );
      }
    };

    // Infinite scroll handler
    const handleScroll = () => {
      const container = scrollContainerRef.value;
      if (
        !container ||
        dataState.value.loadingMore ||
        !dataState.value.pagination.hasMore
      )
        return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollThreshold = 200;

      if (scrollTop + clientHeight >= scrollHeight - scrollThreshold) {
        handleLoadMore();
      }
    };

    // Debounced search
    let searchTimeout: ReturnType<typeof setTimeout>;
    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        loadData();
      }, 300);
    });

    // Watch for tab changes
    watch(activeTab, () => {
      loadData();
    });

    // Watch for date range changes
    watch(dateRange, () => {
      loadData();
    });

    // Watch for status changes
    watch([reservationStatus, requestStatus], () => {
      loadData();
    });

    onMounted(() => {
      loadData();

      const container = scrollContainerRef.value;
      if (container) {
        container.addEventListener("scroll", handleScroll);
      }
    });

    onUnmounted(() => {
      const container = scrollContainerRef.value;
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    });

    return () => (
      <div class="flex flex-1 flex-col overflow-hidden">
        <PageHeader
          activeTab={activeTab.value}
          dateRange={dateRange.value}
          reservationStatus={reservationStatus.value}
          requestStatus={requestStatus.value}
          loading={dataState.value.loading}
          isSearchOpen={isSearchOpen.value}
          searchQuery={searchQuery.value}
          totalCount={totalCount.value}
          statusCounts={statusCounts.value}
          isFiltersOpen={isFiltersOpen.value}
          onTabChange={handleTabChange}
          onDateRangeChange={handleDateRangeChange}
          onReservationStatusChange={handleReservationStatusChange}
          onRequestStatusChange={handleRequestStatusChange}
          onRefresh={loadData}
          onSearchToggle={handleSearchToggle}
          onSearchChange={handleSearchChange}
          onFiltersToggle={handleFiltersToggle}
        />

        <AppSubpage>
          <AppSubpageContent>
            <div
              ref={scrollContainerRef}
              class="h-full overflow-y-auto overflow-x-hidden p-4 xl:p-6"
            >
              {dataState.value.loading ? (
                <LoadingState tab={activeTab.value} />
              ) : dataState.value.error ? (
                <ErrorState
                  tab={activeTab.value}
                  error={dataState.value.error}
                  onRetry={loadData}
                />
              ) : !hasData.value ? (
                <EmptyState
                  tab={activeTab.value}
                  hasDateFilter={dateRange.value.type !== "last_12m"}
                  hasSearchQuery={!!searchQuery.value}
                />
              ) : (
                <ContentList
                  requests={dataState.value.requests}
                  reservations={dataState.value.reservations}
                  isRequestView={isRequestView.value}
                  loadingMore={dataState.value.loadingMore}
                  onRequestClick={handleRequestClick}
                  onReservationClick={handleReservationClick}
                />
              )}
            </div>
          </AppSubpageContent>

          {/* Request Details Sheet */}
          {showRequestDetailsSheet.value && selectedRequest.value && (
            <ReservationRequestDetails
              request={selectedRequest.value}
              linkedReservation={
                selectedRequest.value?.reservation_id
                  ? dataState.value.reservations.find(
                      (res) => res.id === selectedRequest.value?.reservation_id,
                    ) || null
                  : null
              }
              isOpen={showRequestDetailsSheet.value}
              onClose={() => (showRequestDetailsSheet.value = false)}
            />
          )}

          {/* Reservation Details Sheet */}
          {showReservationDetailsSheet.value && selectedReservation.value && (
            <ReservationDetails
              reservation={selectedReservation.value}
              onClose={() => (showReservationDetailsSheet.value = false)}
              onUpdate={handleReservationUpdate}
              onReservationCancel={handleReservationCancel}
            />
          )}

          {/* Mobile Filters Bottom Sheet */}
          {isFiltersOpen.value && (
            <BottomSheet
              isOpen={isFiltersOpen.value}
              title={t("reservations.filters.title")}
              onClose={handleFiltersToggle}
            >
              <div class="p-4 space-y-4">
                <label class="form-control">
                  <div class="label">
                    <span class="label-text flex justify-center items-center">
                      <span class="material-symbols-outlined mr-1">
                        date_range
                      </span>
                      <span>{t("reservations.filters.date_range")}</span>
                    </span>
                  </div>
                  <DateRangePicker
                    value={dateRange.value}
                    onChange={(range) => {
                      handleDateRangeChange(range);
                    }}
                  />
                </label>

                <label class="form-control">
                  <div class="label">
                    <span class="label-text flex justify-center items-center">
                      <span class="material-symbols-outlined mr-1">
                        filter_list
                      </span>
                      <span>{t("reservations.filters.status")}</span>
                    </span>
                  </div>
                  <select
                    class="select select-bordered w-full bg-gray-900 text-accent"
                    value={
                      activeTab.value === "reservations"
                        ? reservationStatus.value
                        : requestStatus.value
                    }
                    onChange={(e) => {
                      const value = (e.target as HTMLSelectElement).value;
                      if (activeTab.value === "reservations") {
                        handleReservationStatusChange(
                          value as ReservationStatus,
                        );
                      } else {
                        handleRequestStatusChange(value as RequestStatus);
                      }
                    }}
                  >
                    <option value="all">
                      {t("reservations.filters.all_statuses")}
                    </option>
                    {activeTab.value === "reservations" ? (
                      <>
                        <option value="confirmed">
                          {t("reservations.status.confirmed")}
                        </option>
                        <option value="seated">
                          {t("reservations.status.seated")}
                        </option>
                        <option value="completed">
                          {t("reservations.status.completed")}
                        </option>
                        <option value="no-show">
                          {t("reservations.status.no_show")}
                        </option>
                        <option value="cancelled_by_host">
                          {t("reservations.status.cancelled_by_host")}
                        </option>
                        <option value="cancelled_by_user">
                          {t("reservations.status.cancelled_by_user")}
                        </option>
                      </>
                    ) : (
                      <>
                        <option value="pending">
                          {t("reservations.status.pending")}
                        </option>
                        <option value="ignored">
                          {t("reservations.status.ignored")}
                        </option>
                        <option value="confirmed">
                          {t("reservations.status.confirmed")}
                        </option>
                        <option value="rejected">
                          {t("reservations.status.rejected")}
                        </option>
                        <option value="cancelled_by_host">
                          {t("reservations.status.cancelled_by_host")}
                        </option>
                        <option value="cancelled_by_user">
                          {t("reservations.status.cancelled_by_user")}
                        </option>
                      </>
                    )}
                  </select>
                </label>
              </div>
            </BottomSheet>
          )}
        </AppSubpage>
      </div>
    );
  },
});
